/**
 * Fetches live product pricing from the booking system's Supabase edge function.
 *
 * The `get-products` function combines Supabase product catalog with Stripe pricing.
 * Response is CDN-cached for 15min by the edge function itself.
 * We add a 5-min Next.js fetch cache on top.
 */

import { formatCurrency } from "@/lib/format"
import { membershipTiers } from "@/lib/pricing/membership-tiers"
import { getCountryConfig, getCountryConfigStatic } from "@/i18n/config"
import { unsoldMriTierKeys } from "@/data/mri-packages"
import type { CmsServiceConfig } from "@/lib/cms"

const SUPABASE_URL =
  process.env.NEXT_PUBLIC_SUPABASE_URL ??
  "https://rnaixxwwvyedkujwijlp.supabase.co"
const SUPABASE_ANON_KEY =
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ??
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJuYWl4eHd3dnllZGt1andpamxwIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjQxNjI2NzgsImV4cCI6MjA3OTUyMjY3OH0.muqVtDgZl00kV2e9hBtaI8vkTRPK5VrVpD9fOhgYU_o"

interface StripePrice {
  unit_amount: number
  currency: string
}

interface Product {
  id: string
  name: string
  product_type: "standard" | "package" | "addon"
  default_price?: StripePrice
  /** Member price from Stripe (get-products feed). Absent when the product has
   *  no distinct member price. This is the sole source of member pricing. */
  member_price?: StripePrice
  duration_minutes?: number
}

/** One membership rhythm as get-products serves it (CHF only, whatever the
 *  `market`). `annual_amount_chf` is CHF cents from the Stripe product's
 *  default yearly price, null when Stripe could not be read. */
interface MembershipTier {
  id: string
  frequency_per_year: number
  stripe_product_id: string
  annual_amount_chf: number | null
}

interface ProductsResponse {
  products: Product[]
  categories: unknown[]
  /** The membership's yearly prices, one per rhythm. Absent on older
   *  deployments of the edge function. */
  membershipTiers?: MembershipTier[]
  timestamp: string
  mode: string
}

/**
 * The membership's three rhythms, keyed by get-products' tier `id` (never by
 * the Stripe product id, which changes per environment). Each one lands in
 * the price maps under a stable key, CHF markets only.
 */
export type MembershipTierId = "once" | "twice" | "four"

export const MEMBERSHIP_TIER_PRICE_KEYS: Record<MembershipTierId, string> = {
  once: "membershipOnce",
  twice: "membershipTwice",
  four: "membershipFour",
}

/** The yearly price per rhythm when Stripe has none to give (CHF, whole
 *  units). The booking app falls back to the same three figures
 *  (`MEMBERSHIP_TIER_DEFAULT_CHF`). Single-sourced in
 *  `lib/pricing/membership-tiers.ts` (`quarterly` is the 4× rhythm): ch is
 *  priced, so a null here means the source lost its market, which must fail
 *  loudly rather than ship a zero. */
const chMembershipTiers = membershipTiers("ch")
if (chMembershipTiers == null) {
  throw new Error("membershipTiers(ch) must be priced")
}
export const MEMBERSHIP_TIER_DEFAULT_CHF: Record<MembershipTierId, number> = {
  once: chMembershipTiers.once,
  twice: chMembershipTiers.twice,
  four: chMembershipTiers.quarterly,
}

const isMembershipTierId = (id: string): id is MembershipTierId =>
  Object.hasOwn(MEMBERSHIP_TIER_PRICE_KEYS, id)

/**
 * The yearly price of one membership rhythm from a price map, or its default
 * when the map has none (an EUR market, a timed-out fetch, a feed without
 * tiers).
 */
export function membershipTierAmount(
  prices: PriceMap | null | undefined,
  id: MembershipTierId
): number {
  const amount = Number(prices?.[MEMBERSHIP_TIER_PRICE_KEYS[id]])
  return amount > 0 ? amount : MEMBERSHIP_TIER_DEFAULT_CHF[id]
}

/** Product name → price map key mapping */
const PRODUCT_PRICE_KEYS: Record<string, string> = {
  // Packages
  Core: "core",
  Advanced: "advanced",
  Pro: "pro",
  // Blood
  "Advanced blood test": "blood",
  // Membership-only follow-up test (member pricing catalog on /membership)
  "Follow-up blood test": "followupBlood",
  // Imaging add-ons
  "Body composition": "addonBodyComposition",
  "Gender health (prostate or ovaries)": "addonGenderHealth",
  "Heart CT calcium scoring (with low-dose radiation)": "addonHeartCT",
  "Mamma MRI (with contrast dye)": "addonMammaMRI",
  "DEXA scan (low-dose radiation)": "addonDEXA",
  // Blood add-ons
  "Essential B-vitamins blood test": "addonBVitamins",
  // Replaced the B-vitamins test in CH (adds homocysteine, AHD-12960); the
  // old one still sells in DE, NL and AT. Each market's feed carries one.
  "Essential B-vitamins & homocysteine blood test": "addonBVitamins",
  "Omega-3 & 6 blood test": "addonOmega",
  "Hormone blood test (Women)": "addonHormoneWomen",
  "Hormone blood test (Men)": "addonHormoneMen",
  "Cortisol blood test": "addonCortisol",
  "Lipoprotein (a) blood test": "addonLpa",
  "PSA blood test": "addonPsa",
  "Trace elements blood test": "addonTraceElements",
  "Heavy metals blood test": "addonHeavyMetals",
  "Coeliac blood test": "addonCoeliac",
}

/**
 * TEST slug → price-map key bridge.
 *
 * The CMS control plane (`products`, since AHD-12802 phase 1) exposes a stable
 * Supabase `bookingProductId` per page-owning row, joined to the rest of the
 * site by the TEST SLUG — the `/tests/<slug>` path tail, which is what the keys
 * below are. The price map, in turn, is keyed by the stable price keys
 * (`blood`, `addonOmega`, …) every pricing consumer depends on. This table
 * bridges the two: given a control-plane row we know which price-map key its
 * Supabase product id should populate, so we can match a Stripe product by its
 * STABLE id instead of its mutable name.
 *
 * Packages (Core/Advanced/Pro) have no control-plane row — they're matched by name
 * only, which is fine: `get-products` gates the whole response on the three
 * package names already, and finance doesn't rename the headline tiers casually.
 * If/when packages get CMS service rows, add them here.
 */
export const PRICE_KEY_FOR_SLUG: Record<string, string> = {
  "advanced-blood-test": "blood",
  // DE's base blood product; see BASE_BLOOD_PRODUCT_IDS for how `blood`
  // resolves there.
  "prevention-starter": "blood",
  "body-composition": "addonBodyComposition",
  "gender-health": "addonGenderHealth",
  "calcium-score": "addonHeartCT",
  "mamma-mri": "addonMammaMRI",
  "dexa-scan": "addonDEXA",
  "essential-b-vitamins-test": "addonBVitamins",
  "omega-blood-test": "addonOmega",
  "hormone-blood-test-women": "addonHormoneWomen",
  "hormone-blood-test-men": "addonHormoneMen",
  "cortisol-blood-test": "addonCortisol",
  lpa: "addonLpa",
  "psa-blood-test": "addonPsa",
  "trace-elements-blood-test": "addonTraceElements",
  "heavy-metals-blood-test": "addonHeavyMetals",
  "coeliac-blood-test": "addonCoeliac",
}

/**
 * Markets whose base blood check-up is NOT the Advanced blood test, keyed to
 * the Supabase product ids that carry that product's Stripe price.
 *
 * Germany sells the Vorsorge-Starter (`prevention-starter`) instead. Its
 * Stripe price (199 € in the CMS `pricing-variables` mirror) is not in the
 * `get-products` feed yet, and the product is not bookable, so its detail page
 * shows a waitlist and no price. For these markets `blood` resolves ONLY from
 * these ids: the Advanced test's price never stands in for it, and the fallback
 * table carries no `blood` figure. Once `get-products` serves one of these ids,
 * every `blood` consumer (the DE city LPs, /pricing, the product cards, the
 * Vorsorge-Starter page) shows the live figure with no code change.
 */
const BASE_BLOOD_PRODUCT_IDS: Record<string, ReadonlySet<string>> = {
  de: new Set([
    "7025029e-de00-40de-b0de-6e1d57a27e25", // prevention-starter
    "7025029e-de00-40de-b0de-6e1d57a27e26", // prevention-starter-product
  ]),
  // The Netherlands keeps the "Advanced blood test" name and page, but sells
  // the reduced 65-marker panel (AHD-13560). The old Advanced product
  // stays in the NL feed until launch, so it must never claim `blood` here.
  // Ids are fixed in ahead-booking-new's
  // 20261014120000_add_advanced_blood_test_nl.sql (#654); the feed serves
  // them once the launch migration flips the rows to `active`.
  nl: new Set([
    "249b100d-7e57-4a11-9e1d-000000000025", // advanced-blood-test-nl
    "249b100d-7e57-4a11-9e1d-000000000026", // advanced-blood-test-nl-product
  ]),
}

/** Price keys only one test page maps to. `blood` is shared by the Advanced
 *  blood test and the Vorsorge-Starter, so its live product can't say which
 *  page it books. */
const SINGLE_PAGE_PRICE_KEYS = new Set(
  Object.values(PRICE_KEY_FOR_SLUG).filter(
    (key, i, keys) => keys.indexOf(key) === keys.lastIndexOf(key)
  )
)

/**
 * The booking product a test page's CTA should use in this market.
 *
 * The CMS `bookingProductId` is one id for every market, but a market can sell
 * a different product under the page's price key: in CH the B-vitamins &
 * homocysteine add-on replaced the Essential B-vitamins test (AHD-12960), so
 * the CMS id is not in the Swiss feed. When the CMS product is not among this
 * market's live products, use the live product for the page's price key;
 * otherwise, or with no live feed, keep the CMS id.
 */
export function marketBookingProductId(
  slug: string,
  cmsBookingProductId: string | undefined,
  liveProductIds: PriceMap
): string | undefined {
  const key = PRICE_KEY_FOR_SLUG[slug]
  if (!cmsBookingProductId || !key || !SINGLE_PAGE_PRICE_KEYS.has(key))
    return cmsBookingProductId
  if (Object.values(liveProductIds).includes(cmsBookingProductId))
    return cmsBookingProductId
  return liveProductIds[key] ?? cmsBookingProductId
}

/** CMS control-plane config as passed to the price resolver: the map keyed by slug
 *  that `getServicesConfig()` returns, or the raw array. */
export type ServicesConfigInput =
  | ReadonlyMap<string, CmsServiceConfig>
  | readonly CmsServiceConfig[]

/**
 * Build a Supabase-product-id → price-map-key lookup from the CMS products
 * control plane. Lets `fetchPricesFromSupabase` resolve a Stripe product by its
 * stable `id` (rename-proof) instead of its display `name`. Only services that
 * have BOTH a populated `bookingProductId` AND a known price-map key (via
 * `PRICE_KEY_FOR_SLUG`) contribute; everything else is left to the name-match
 * fallback, so an unpopulated CMS field never breaks resolution.
 */
function buildIdToPriceKey(
  config: ServicesConfigInput | undefined
): Map<string, string> {
  const idToKey = new Map<string, string>()
  if (!config) return idToKey
  const rows = config instanceof Map ? Array.from(config.values()) : config
  for (const row of rows as readonly CmsServiceConfig[]) {
    const id = row.bookingProductId
    if (!id) continue
    const key = PRICE_KEY_FOR_SLUG[row.slug]
    if (!key) continue
    idToKey.set(id, key)
  }
  return idToKey
}

export interface PriceMap {
  /** Raw price amount without currency prefix (e.g. "1990" or "299") */
  [key: string]: string
}

/**
 * Member prices are piggybacked into the same `prices` / `fullPrices` maps under
 * a `member:`-prefixed key (nothing iterates these maps blindly, so this is
 * safe and avoids threading a parallel map through the whole quiz client tree).
 * `prices["member:addonOmega"]` = raw member amount; `fullPrices["member:…"]` =
 * the formatted label. Sourced solely from Stripe's `member_price`; absent for
 * products with no member price — today that is every EUR-market product, since
 * Stripe carries no EUR member prices yet. Read via the helpers in
 * `@/lib/pricing/member-prices`.
 */
export const memberPriceMapKey = (key: keyof PriceMap) => `member:${key}`

export interface PriceData {
  /** Prices formatted without currency (raw amount string) */
  prices: PriceMap
  /** Prices with localized currency formatting (e.g. "CHF 1'990.00" or "1.299,00 €") */
  fullPrices: PriceMap
  /** Currency code */
  currency: string
  /** Booking product UUID by price-map key (e.g. addonOmega → "fa77…"). Live from get-products. */
  productIds: PriceMap
  /**
   * `"live"` only when the figures came from the pricing feed on the server.
   * Absent on the static seed (`buildFallback`) and the no-price shapes, so a
   * surface that states a price as a fact can refuse anything else.
   */
  source?: "live"
}

/**
 * Request-time bound (ms) for the Supabase prices fetch on /tests pages.
 *
 * The test-detail and single-biomarker pages await prices server-side before
 * the shell can render; on a slow Supabase the whole page stalls (this is
 * what timed out e2e `cross-catalog-suggestion`, which clicks through to a
 * test page). Callers pass it as `opts.timeoutMs` so a slow feed resolves to
 * the no-price result instead of blocking the page. Membership/teams/pricing
 * callers pass no opts: unbounded at request time, bounded by
 * BUILD_PRICES_TIMEOUT_MS during `next build`.
 */
export const TESTS_PRICES_TIMEOUT_MS = 3000

/**
 * Bound for the Supabase fetch during `next build` static prerender, when the
 * caller passes no `opts.timeoutMs`. The build MUST be bounded: an unbounded
 * build-time wait hangs the page past Next's 60 s static-generation timeout
 * when Supabase stalls, failing the whole build on every retry (2026-10-06
 * deploy-vercel-preview flake). Generous on purpose — 15 s of Supabase stall
 * is already a proven outage, not a slow feed — and a build-time timeout
 * degrades to the fallback table, never to priceless.
 */
export const BUILD_PRICES_TIMEOUT_MS = 15_000

/**
 * Buffer (ms) over the fetch bound that the settle guard allows before it
 * resolves the call itself.
 *
 * `fetchPricesFromSupabase` is otherwise bounded ONLY by the `AbortSignal`
 * passed into fetch — and under `next build` that signal does not cover the
 * whole wait. Next 16's patched fetch first awaits
 * `incrementalCache.lock(cacheKey)`, an in-process per-URL+body lock no
 * AbortSignal reaches, and reads the cached body via a tee. A wait there
 * never settles and never throws: the abort deadline fires unheard, no
 * timeout path runs, and pages that await prices burn Next's 60 s
 * static-generation timeout on all 3 attempts with nothing in the log naming
 * prices (2026-10-13 preview-build stalls on tests/psa-blood-test and
 * tests/lpa — the same class of hang `SETTLE_GUARD_MS` in lib/cms.ts covers
 * for CMS reads).
 *
 * The guard races the WHOLE body (fetch + json + mapping) against
 * `timeoutMs + PRICES_SETTLE_GUARD_BUFFER_MS` and resolves the same
 * degradation the timeout path returns, so a wedged price fetch degrades the
 * page instead of the build. The buffer covers abort-delivery latency only —
 * a healthy fetch always settles first via its own paths.
 */
export const PRICES_SETTLE_GUARD_BUFFER_MS = 2000

/** Opt-in knobs for `fetchPricesFromSupabase`. All optional; the default call
 *  (no opts) keeps its previous request-time behaviour. */
export interface FetchPricesOptions {
  /**
   * Bound the Supabase fetch to this many milliseconds. On timeout the call
   * resolves to the no-price result (empty maps in the market's currency —
   * the same shape returned for a market with no authoritative pricing) and
   * logs one `console.warn`, so the page renders without a price line rather
   * than stalling. Other failures still degrade to the fallback table.
   *
   * During `next build` static prerender (`NEXT_PHASE ===
   * "phase-production-build"`) a timeout instead resolves to the FALLBACK
   * table (never priceless — the baked page must carry figures), and callers
   * without opts are bounded by BUILD_PRICES_TIMEOUT_MS rather than waiting
   * unbounded.
   */
  timeoutMs?: number
}

/** Raw fallback prices in CHF (whole units, not cents) */
const FALLBACK_PRICES_CHF: Record<string, number> = {
  core: 1990,
  advanced: 2490,
  pro: 3549,
  blood: 299,
  addonBodyComposition: 100,
  addonGenderHealth: 150,
  addonHeartCT: 470,
  addonMammaMRI: 800,
  addonDEXA: 135,
  // B12, folate and homocysteine in CH (AHD-12960); Stripe's price wins.
  addonBVitamins: 89,
  addonOmega: 129,
  addonHormoneWomen: 149,
  addonHormoneMen: 149,
  // Single-marker add-ons. EUR only where signed off (NL, in
  // FALLBACK_PRICES_EUR): Stripe carries no
  // market-tagged EUR price for them, and inventing one is worse than hiding
  // the line (same reasoning as the imaging add-ons).
  addonCortisol: 29,
  addonPsa: 19,
  addonLpa: 29,
  // CH-only blood add-ons (P-AHD-333), mirroring Stripe.
  addonTraceElements: 225,
  addonHeavyMetals: 285,
  addonCoeliac: 49,
  // The membership's yearly price per rhythm (MEMBERSHIP_TIER_PRICE_KEYS).
  membershipOnce: MEMBERSHIP_TIER_DEFAULT_CHF.once,
  membershipTwice: MEMBERSHIP_TIER_DEFAULT_CHF.twice,
  membershipFour: MEMBERSHIP_TIER_DEFAULT_CHF.four,
}

/**
 * Raw fallback prices in EUR (whole units, not cents) — the degradation net
 * for launched EUR markets when the live feed is unavailable, mirroring
 * FALLBACK_PRICES_CHF's role for CH. The live source is `get-products` with
 * the `market` param (Stripe prices tagged with the country's ISO-2 code), and
 * each table must match that market's tagged Stripe prices 1:1.
 *
 * **EUR is a currency, not a price list.** Keyed per market because each one
 * prices against its own competitive set, not as a discount off CHF: Austria's
 * ceiling is what the most expensive Austrian check-up costs (~€2,300), and its
 * lab work is cheaper than Switzerland's, so its ladder is built from Austrian
 * numbers and shares nothing with NL/DE beyond the symbol. A single shared EUR
 * table would have published Austrian package prices on the Dutch and German
 * sites the moment Austria got figures.
 *
 * A market only carries keys for products it actually sells. Austria sells the
 * three MRI package tiers and nothing else, so it has no `blood` or add-on
 * entries; NL and DE sell the blood check-up and its add-ons and have no
 * package entries. A missing key means "no authoritative EUR figure" and the
 * UI must say so rather than show a number — the quiz result renders a "price
 * coming soon" label, and `mri-package-cards.tsx` renders its empty holding
 * line. Never fill a gap with a converted CHF figure: €2,490 is not worth
 * CHF 2,490, and that placeholder was tried and reverted once already.
 */
const FALLBACK_PRICES_EUR: Record<string, Record<string, number>> = {
  // NL and DE happen to carry identical figures today; they are separate
  // markets and are written out separately so one can move without the other.
  nl: {
    blood: 299,
    addonBVitamins: 49,
    addonOmega: 129,
    addonHormoneWomen: 149,
    addonHormoneMen: 149,
    // Single-marker add-ons, signed off for NL only (2026-09-28). Stripe
    // carries no market-tagged EUR price for them elsewhere, and inventing one
    // is worse than hiding the line.
    addonLpa: 29,
    addonCortisol: 29,
  },
  de: {
    blood: 299,
    addonBVitamins: 49,
    addonOmega: 129,
    addonHormoneWomen: 149,
    addonHormoneMen: 149,
  },
  // No Core: Austria sells Advanced + Pro only (AHD-13988, see
  // `mriPackagesFor` in data/mri-packages.ts).
  at: {
    advanced: 1950,
    pro: 2350,
  },
}

/**
 * The products each market leads with, and therefore the ones whose live
 * prices must all resolve before a feed counts as trustworthy. Not every key a
 * market prices: an add-on the feed hasn't got yet is a missing price line,
 * while a missing headline product means the feed is broken and the whole
 * market should fall back rather than render half a price list.
 *
 * Exported so `__tests__/pricing-eur-markets.test.ts` can assert the invariant
 * that keeps the two maps in step: every key here must exist in that market's
 * fallback table, or the gate would demand a price the degradation net cannot
 * supply and the market would fall back forever.
 */
export const HEADLINE_PRICE_KEYS: Record<string, string[]> = {
  ch: ["core", "advanced", "pro"],
  // No `de` or `nl` entry on purpose. Germany's base blood product is the
  // Vorsorge-Starter and the Netherlands' is the reduced Advanced blood test
  // (AHD-13560), not the Advanced test the table prices, so `buildFallback`
  // skips the table's `blood` figure for them and the live feed is their only
  // price source. The gate routes every `BASE_BLOOD_PRODUCT_IDS` market down the
  // other branch for exactly that reason, so a headline list here would never
  // be read — and listing a key the fallback withholds would assert an
  // invariant that cannot hold.
  // Austria sells no blood product at all, so gating it on `blood` — as this
  // did for every EUR market — meant a live feed could never be judged
  // complete there.
  at: ["advanced", "pro"],
}

/** Whether a country has authoritative pricing we can show. Any ENABLED market
 *  qualifies: a waitlist market advertises its real figures with waitlist CTAs
 *  (2026-07-29 decision — the blood check-up shows its price pre-launch so
 *  people know what they're joining the waitlist for), and `built` /
 *  `checkup-preview` previews render real prices behind the token gate.
 *  Products whose Stripe prices don't exist yet simply come back absent from
 *  the market-tagged feed and their price lines stay hidden. */
function hasAuthoritativePricing(country: string): boolean {
  return getCountryConfig(country) !== undefined
}

/**
 * Build a PriceData object from raw price numbers for a given country.
 *
 * Exported so client components can render correct currency + amounts on
 * the first paint (before `fetchPrices` resolves), instead of flashing CHF
 * figures on EUR-currency markets like /de/de.
 *
 * EUR markets that aren't ENABLED at all return empty `prices`/`fullPrices` so
 * consumers can hide the price line entirely rather than fall back to (a)
 * fabricated EUR figures, or (b) mislabel CHF amounts with €. Enabled markets
 * (waitlist included) get the EUR table below as the pre-feed seed.
 */
export function buildFallback(country: string): PriceData {
  const cfg = getCountryConfigStatic(country)
  const currency = cfg?.currency ?? "CHF"
  if (currency === "EUR" && !hasAuthoritativePricing(country)) {
    return { prices: {}, fullPrices: {}, currency, productIds: {} }
  }
  // One market's table, never a merge of two. `preflight` layered per-market
  // figures over a shared EUR base; that shape cannot express Austria, which
  // must inherit NOTHING — a spread would have handed it the base table's
  // `blood: 299` and put a blood price on a market that sells no blood.
  //
  // An ENABLED EUR market with no table is a config error, not a degraded
  // state: `hasAuthoritativePricing` already returned early for the markets
  // that legitimately have no figures, so reaching here without one means a
  // market was enabled and never priced. Let it throw rather than silently
  // render price-free — `__tests__/pricing-eur-markets.test.ts` catches it
  // first.
  const raw =
    currency === "EUR" ? FALLBACK_PRICES_EUR[country] : FALLBACK_PRICES_CHF
  const prices: PriceMap = {}
  const fullPrices: PriceMap = {}
  for (const [key, amount] of Object.entries(raw)) {
    // The table's `blood` is the Advanced test's figure, not this market's.
    if (key === "blood" && BASE_BLOOD_PRODUCT_IDS[country]) continue
    prices[key] = String(amount)
    fullPrices[key] = formatCurrency(amount, country)
  }
  return { prices, fullPrices, currency, productIds: {} }
}

/** Format a Stripe cents amount as a localized currency string. */
function formatStripeAmount(unitAmount: number, country: string): string {
  return formatCurrency(unitAmount / 100, country)
}

/** Sync fallback for client UI (e.g. search popular rows) before network resolves. */
export function fallbackFullPrice(
  country: string | undefined,
  mapKey: string
): string {
  const fp = buildFallback(country ?? "ch").fullPrices[mapKey]
  return fp ?? ""
}

/**
 * Server-side fetch against the Supabase edge function. Used directly by
 * server components and proxied by `/api/prices` for client callers.
 *
 * Don't call this from a client component — it embeds the Supabase host in
 * the request, which is on common ad-blocker / extension denylists and was
 * the source of `TypeError: Failed to fetch` Sentry noise (issue 117794612).
 * Use `fetchPrices` instead, which routes through the same-origin proxy
 * when called in the browser.
 */
export async function fetchPricesFromSupabase(
  country: string,
  servicesConfig?: ServicesConfigInput,
  opts?: FetchPricesOptions
): Promise<PriceData> {
  const fallback = buildFallback(country)
  const cfg = getCountryConfigStatic(country)
  const currency = (cfg?.currency ?? "CHF").toUpperCase()

  // EUR markets that aren't enabled show no prices (see buildFallback) —
  // skip the fetch entirely.
  if (currency === "EUR" && !hasAuthoritativePricing(country)) {
    return fallback
  }

  // Bounded always during `next build` static prerender: an unbounded
  // build-time wait hangs the page past Next's 60 s static-generation
  // timeout when Supabase stalls and fails the whole build (2026-10-06
  // deploy-vercel-preview flake). Callers with opts keep their own bound; a
  // build-time timeout degrades to the fallback table (never priceless —
  // see the catch below). Request-time callers without opts (membership,
  // teams, pricing, /api/prices) get no signal and are unaffected.
  const isBuildPhase = process.env.NEXT_PHASE === "phase-production-build"
  const timeoutMs = isBuildPhase
    ? (opts?.timeoutMs ?? BUILD_PRICES_TIMEOUT_MS)
    : opts?.timeoutMs
  // `AbortSignal.timeout()` rejects non-integer delays with a RangeError
  // (same rounding as `cmsGetWithStatus` in lib/cms.ts).
  const boundMs =
    typeof timeoutMs === "number" && Number.isFinite(timeoutMs) && timeoutMs > 0
      ? Math.round(timeoutMs)
      : undefined
  const timeoutSignal =
    boundMs === undefined ? undefined : AbortSignal.timeout(boundMs)

  // The whole fetch + json + mapping body, split out (not inlined in the
  // race below) so the export can race it against the settle guard — the
  // body itself is unchanged (same shape as `cmsGetWithStatus` over
  // `cmsGetWithStatusInner` in lib/cms.ts).
  const run = async (): Promise<PriceData> => {
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/get-products`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
        ...(timeoutSignal ? { signal: timeoutSignal } : {}),
        // `market` selects the country's Stripe prices: get-products resolves
        // prices tagged with the ISO-2 country code in Stripe price metadata and
        // omits products that carry no tagged price for that market. CH is the
        // default market (Stripe `default_price`); omitting the param there keeps
        // the edge function's legacy warm cache key. Next's fetch cache keys on
        // the body, so each market caches independently under the shared tag.
        body: JSON.stringify(
          country === "ch"
            ? { language: "en-US" }
            : { language: "en-US", market: country }
        ),
        // 24h ceiling, not a freshness target. The `prices` tag is busted
        // on demand: the CMS runs a change-detected hourly Stripe sync
        // (`/api/sync-prices` → `pricing-variables` rows), and any row it
        // actually writes fires the Payload afterChange hook → POST
        // /api/revalidate → `revalidateTag("prices")`. A real price change
        // therefore lands within the cron's hour, not this window.
        //
        // The old `300` was the binding constraint on ISR for every page that
        // renders a price server-side (~490 `tests/v/[code]/[slug]`
        // permutations plus pricing/membership/quiz/quiz-focus): Next takes the
        // MINIMUM fetch revalidate in a route as that route's regeneration
        // period, so the whole set regenerated every 5 minutes under traffic.
        // Client-rendered prices are unaffected either way — they read
        // `/api/prices`, which keeps its own `s-maxage=300` CDN window.
        next: { revalidate: 86400, tags: ["prices"] },
      })

      if (!res.ok) return fallback

      const data: ProductsResponse = await res.json()
      const prices: PriceMap = {}
      const fullPrices: PriceMap = {}
      const productIds: PriceMap = {}

      // Prefer the stable Supabase product id (from the CMS control plane) over
      // the mutable product name. A rename in Stripe/Supabase then can't silently
      // drop a price; the name map stays as the fallback for products with no CMS
      // `bookingProductId` populated (and for the headline packages, which have no
      // CMS service row).
      const idToKey = buildIdToPriceKey(servicesConfig)

      // Never let an amount denominated in another currency masquerade as this
      // market's — a CHF figure formatted with € would be silently wrong. Today
      // get-products omits untagged prices on non-default markets, so the
      // mismatch arm is pure defense.
      const inMarketCurrency = (price: StripePrice) =>
        price.currency.toUpperCase() === currency

      const assign = (key: string, product: Product) => {
        productIds[key] = product.id
        if (product.member_price && inMarketCurrency(product.member_price)) {
          const mk = memberPriceMapKey(key)
          prices[mk] = String(product.member_price.unit_amount / 100)
          fullPrices[mk] = formatStripeAmount(
            product.member_price.unit_amount,
            country
          )
        }
        if (!product.default_price || !inMarketCurrency(product.default_price))
          return
        const { unit_amount } = product.default_price
        prices[key] = String(unit_amount / 100)
        fullPrices[key] = formatStripeAmount(unit_amount, country)
      }

      // Markets with their own base blood product take `blood` from its ids
      // only; the Advanced test (by id or by name) never claims it there.
      const baseBloodIds = BASE_BLOOD_PRODUCT_IDS[country]
      // An MRI tier the market doesn't sell (Core in Austria) is pre-claimed so
      // no product, by id or by name, can give it a price.
      const claimed = new Set<string>(unsoldMriTierKeys(country))
      if (baseBloodIds) {
        claimed.add("blood")
        const base = data.products.find((p) => baseBloodIds.has(p.id))
        if (base) assign("blood", base)
      }

      // Pass 1: id-matches are authoritative — a stale duplicate that still
      // carries the old name must NOT later clobber a key already pinned by id.
      for (const product of data.products) {
        const key = idToKey.get(product.id)
        if (!key || claimed.has(key)) continue
        assign(key, product)
        claimed.add(key)
      }
      // Pass 2: name-match fallback for any key not already claimed by an id.
      for (const product of data.products) {
        const key = PRODUCT_PRICE_KEYS[product.name]
        if (!key || claimed.has(key)) continue
        assign(key, product)
      }

      // The membership tiers are CHF prices whatever market was asked for, so
      // only a CHF market takes them. A tier Stripe could not price (null), or
      // a feed without tiers, keeps its default figure.
      if (currency === "CHF") {
        const live = new Map<MembershipTierId, number>()
        for (const tier of data.membershipTiers ?? []) {
          const cents = tier.annual_amount_chf
          if (!isMembershipTierId(tier.id)) continue
          if (typeof cents !== "number" || !(cents > 0)) continue
          live.set(tier.id, cents / 100)
        }
        for (const id of Object.keys(
          MEMBERSHIP_TIER_PRICE_KEYS
        ) as MembershipTierId[]) {
          const amount = live.get(id) ?? MEMBERSHIP_TIER_DEFAULT_CHF[id]
          const key = MEMBERSHIP_TIER_PRICE_KEYS[id]
          prices[key] = String(amount)
          fullPrices[key] = formatCurrency(amount, country)
        }
      }

      // Integrity gate, per market shape: a market's headline products must all
      // resolve, or we assume a broken feed and degrade to the fallback.
      //
      // Which products those are is per market (`HEADLINE_PRICE_KEYS`), not one
      // rule for each currency. Hardcoding `prices.blood` for every EUR market,
      // as this did, meant Austria could never pass: it sells no blood product,
      // so a live feed was always judged broken and always degraded to the
      // fallback.
      // A market whose base blood product may not be priced yet can't gate on
      // its table at all; any resolved product proves the feed answered.
      const complete = baseBloodIds
        ? Object.keys(productIds).length > 0
        : HEADLINE_PRICE_KEYS[country].every((key) => Boolean(prices[key]))
      if (complete) {
        return { prices, fullPrices, currency, productIds, source: "live" }
      }

      return fallback
    } catch (err) {
      // A bounded fetch that hits its deadline aborts with a TimeoutError: the
      // page renders WITHOUT a price line (the no-authoritative-price shape —
      // same empty maps the disabled-EUR early return produces), exactly as for
      // a test the feed has no price for. Every other failure keeps the old
      // behaviour (fallback table). Duck-typed `name` check, not instanceof:
      // the rejection crosses undici/Next runtime realms.
      if (
        timeoutSignal &&
        (err as { name?: unknown } | null)?.name === "TimeoutError"
      ) {
        if (isBuildPhase) {
          // The baked page must carry figures: degrade to the fallback table,
          // the same degradation every other build-time failure takes.
          console.warn(
            `[pricing] Supabase get-products fetch timed out after ${timeoutMs}ms during build for market "${country}" — baking fallback prices`
          )
          return fallback
        }
        console.warn(
          `[pricing] Supabase get-products fetch timed out after ${timeoutMs}ms for market "${country}" — rendering without prices`
        )
        return { prices: {}, fullPrices: {}, currency, productIds: {} }
      }
      return fallback
    }
  }

  // Unbounded request-time calls (no opts outside the build) run the body
  // bare, exactly as before: no guard, no extra timer.
  if (boundMs === undefined) return run()

  // Settle guard: the fetch above is bounded only by an AbortSignal, which
  // never fires on a wait it cannot reach (Next's patched-fetch
  // incremental-cache lock and tee — see PRICES_SETTLE_GUARD_BUFFER_MS).
  // Race the whole body against bound + buffer; on expiry resolve the same
  // degradation the timeout path returns (fallback table at build time, the
  // no-price shape at request time) so a wedged fetch degrades the page
  // instead of burning Next's 60 s static-generation timeout. The timer is
  // cleared when the real work settles first — never left dangling — and
  // stays ref'd so the guard itself keeps the event loop alive until it fires.
  const guardMs = boundMs + PRICES_SETTLE_GUARD_BUFFER_MS
  let guard: ReturnType<typeof setTimeout> | undefined
  return Promise.race([
    run().finally(() => {
      if (guard !== undefined) clearTimeout(guard)
    }),
    new Promise<PriceData>((resolve) => {
      guard = setTimeout(() => {
        const when = isBuildPhase ? "during build" : "at request time"
        console.warn(
          `[pricing] Supabase get-products did not settle within ${guardMs}ms for market "${country}" (${when}) — ` +
            (isBuildPhase
              ? "baking fallback prices"
              : "rendering without prices")
        )
        resolve(
          isBuildPhase
            ? fallback
            : { prices: {}, fullPrices: {}, currency, productIds: {} }
        )
      }, guardMs)
    }),
  ])
}

/**
 * Fetch live prices. On the server, hits Supabase directly. In the browser,
 * goes through the same-origin `/api/prices` proxy so ad-blockers and
 * network filters that target the Supabase host can't drop the request.
 *
 * @param country - Country code (e.g. "ch", "nl", "de"). Launched EUR markets
 *   resolve their market-tagged Stripe prices; pre-launch EUR markets return
 *   empty maps so consumers hide the price line.
 * @param servicesConfig - CMS control plane from `products` (test slug → row,
 *   see `getServicesConfig`). When
 *   supplied on the server, prices/products resolve by stable Supabase id
 *   first, falling back to name-match. Ignored in the browser path, which
 *   proxies through `/api/prices` (that route loads the config server-side).
 */
export async function fetchPrices(
  country: string,
  servicesConfig?: ServicesConfigInput
): Promise<PriceData> {
  if (typeof window === "undefined") {
    return fetchPricesFromSupabase(country, servicesConfig)
  }

  const fallback = buildFallback(country)
  const cfg = getCountryConfigStatic(country)
  const currency = (cfg?.currency ?? "CHF").toUpperCase()

  // EUR markets that aren't enabled show no prices — same skip as the server.
  if (currency === "EUR" && !hasAuthoritativePricing(country)) {
    return fallback
  }

  try {
    const res = await fetch(
      `/api/prices?country=${encodeURIComponent(country)}`
    )
    if (!res.ok) return fallback
    return (await res.json()) as PriceData
  } catch {
    return fallback
  }
}
